import { analyzeAudio } from "../analysisService";
import { getAICoachingAdvice } from "../aiCoachingService";
import { analyzeBiomechanicalMatch } from "../visionService";
import {
  AnalysisResult,
  AnalysisStage,
  RecordingMetadata,
  MatchType,
  GUITAR_LEVELS,
  CalibrationStatus,
  IntegrityStatus
} from "../../types";
import { computeSHA256 } from "../../utils/hashUtils";
import { integrityGate, checkSpectralSimilarity } from "../integrityGate";
import { sealResult } from "../../policy/integrityProtocol";
import { normalizeHardwareLagMs } from "../latencyUnits";
import { setLastResult as setLastResultCache } from "../analysisCache";
import { buildTrustedScore } from "../trustBoundary";

// Valeur mesurée sur Mac Speakers (latence matérielle réelle)
const HARDWARE_OUTPUT_LATENCY_MS = 264.5;

export interface AnalysisPipelineConfig {
  refFile: File | null;
  studentFile: File;
  targetBpm: number;
  latencyMs: number;
  lang: "fr" | "en";
  matchType: MatchType;
  grid: string;
  timeSignature: any;
  levelId: number;
  soundProfile: any;
  metadata?: RecordingMetadata;
  qualityWarning?: string;
  activeMission?: any;
  onStageChange: (stage: AnalysisStage, extra?: string) => void;
}

/**
 * 🔒 EXTRACTION STRICTE DES EMPREINTES SPECTRALES RUNTIME
 * - AUCUN fallback
 * - AUCUNE reconstruction
 * - AUCUNE lecture depuis result.tone.*
 *
 * Source UNIQUE autorisée :
 *   session.metrics.debug.stuFingerprint / refFingerprint
 */
function extractSpectralFingerprints(
  result: AnalysisResult
): { stu: number[]; ref: number[] } {
  const dbg: any = result.session?.metrics?.debug;
  if (!dbg) return { stu: [], ref: [] };

  const safeVec = (v: any): number[] => {
    if (!Array.isArray(v)) return [];
    const nums = v.map(Number).filter(Number.isFinite);
    return nums.length > 0 ? nums : [];
  };

  const stu = safeVec(dbg.stuFingerprint);
  const ref = safeVec(dbg.refFingerprint);

  return { stu, ref };
}

export class AnalysisOrchestrator {
  static async runFullLifecycle(
    config: AnalysisPipelineConfig
  ): Promise<AnalysisResult> {
    const {
      refFile,
      studentFile,
      targetBpm,
      latencyMs,
      lang,
      matchType,
      grid,
      timeSignature,
      levelId,
      metadata,
      soundProfile,
      onStageChange
    } = config;

    try {
      /* =======================
       * 1) DECODING + HASH
       * ======================= */
      onStageChange("DECODING");

      const stuArrayBuffer = await studentFile.arrayBuffer();
      const stuHash = await computeSHA256(stuArrayBuffer);

      let refHash = "none";
      if (refFile) {
        const refArrayBuffer = await refFile.arrayBuffer();
        refHash = await computeSHA256(refArrayBuffer);
      }

      /* =======================
       * 2) CERTIFICATION GATE
       * ======================= */
      onStageChange("CERTIFYING");

      const source = (metadata?.source as any) || "UPLOAD";
      const gateDecision = integrityGate({
        origin: source,
        stuHash,
        refHash,
        captureToken: metadata?.captureToken,
        mode:
          matchType === "ARENA" || matchType === "LEAGUE"
            ? matchType
            : "ANALYZE"
      });

      /* =======================
       * 3) CALIBRATION
       * ======================= */
      const pureHardwareLag = normalizeHardwareLagMs(
        latencyMs + HARDWARE_OUTPUT_LATENCY_MS
      );

      let calibStatus: CalibrationStatus = "VALID";
      if (source === "REAL_AUDIO" && pureHardwareLag <= 0) {
        calibStatus = "INVALID";
      }

      const activeLevel =
        GUITAR_LEVELS.find(l => l.id === levelId) || GUITAR_LEVELS[3];

      /* =======================
       * 4) DSP AUDIO ANALYSIS
       * ======================= */
      let result = await analyzeAudio({
        studentFile,
        referenceFile: refFile,
        targetBpm,
        matchType,
        level: activeLevel as any,
        grid,
        capture: { studentSource: source, timeSignature, soundProfile },
        hardwareLagMs: pureHardwareLag,
        startOffsetMs: 0,
        qualityWarning: config.qualityWarning,
        onStageChange,
        isAuthorizedForScoring: gateDecision.status === "PASS",
        calibrationStatus: calibStatus,
        metadata
      });
      /* =======================
 * 4.1) DURATION TRUTH LOCK (UI sync)
 * ======================= */
/**
 * ✅ Verrouille une durée unique "vérité" au niveau session
 * - sert aux charts / vidéo / projections
 * - évite tout recalcul depuis envelope.length côté UI
 */
const d = Number((result as any)?.session?.duration);
if (!Number.isFinite(d) || d <= 0) {
  console.warn("[ORCH] session.duration invalide. Fallback=1s (à corriger côté DSP).", d);
  (result as any).session.duration = 1;
}

// Expose aussi un champ explicite pour le DSP adapter (si tu veux)
(result as any).durationSec = (result as any).session.duration; // compat pickDurationSec()

      /* =======================
       * 5) VISION (NON CERTIFIANT)
       * ======================= */
      const isVideo =
        studentFile.type.includes("video") ||
        /\.(mp4|mov|webm)$/i.test(studentFile.name);

      if (isVideo && gateDecision.status === "PASS") {
        onStageChange("IA_CORE", "Video DSP Engine...");
        try {
          const bio = await analyzeBiomechanicalMatch(
            studentFile,
            refFile,
            result.session.duration,
            lang
          );
          result.biomechanics = bio;
          result.session.metrics.posture = bio.score;
        } catch (vErr) {
          console.warn("[VISION] Kinematic analysis failed.", vErr);
        }
      }

      /* =======================
       * 6) ANTI-CLONE SPECTRAL (RUNTIME ONLY)
       * ======================= */
      const { stu, ref } = extractSpectralFingerprints(result);
      const spectralDecision = checkSpectralSimilarity(stu, ref);

      /* =======================
       * 7) TRUST BOUNDARY
       * ======================= */
      const trusted = buildTrustedScore({
        scoreCalculated: result.session.score,
        isAuthorizedForScoring: gateDecision.status === "PASS",
        hasServerProof: false,
        usedClientOnlyFlags: false
      });

      (result.session.truth_report as any).verification_level =
        trusted.verificationLevel;

      let integrityStatus: IntegrityStatus = trusted.integrityStatus;

      /* =======================
       * 8) HARD INVALIDATION
       * ======================= */
      if (spectralDecision.status === "FAIL") {
        integrityStatus = "FAIL";
        result.session.score = null;
        result.session.match_score = null;
        result.session.certified = false;
        result.session.truth_report.integrity_status = "FAIL";
        result.session.truth_report.calibration_status = calibStatus;
        result.session.truth_report.status = "INVALID";
        result.session.truth_report.ux_message =
          "Analyse bloquée : similarité spectrale suspecte";
      } else {
        result.session.score = trusted.scoreDisplayed;
        result.session.match_score = trusted.scoreDisplayed;
        result.session.certified =
          integrityStatus === "PASS" && calibStatus === "VALID";
        result.session.truth_report.integrity_status = integrityStatus;
        result.session.truth_report.calibration_status = calibStatus;
        result.session.truth_report.ux_message = result.session.certified
          ? "Analyse vérifiée"
          : "Analyse indicative";
      }

      /* =======================
       * 9) AI COACHING (NON CERTIFIANT)
       * ======================= */
      onStageChange("IA_CORE");
      try {
        const isPremium = localStorage.getItem("gsc_is_premium") === "true";
        const aiAdvice = await getAICoachingAdvice(
          result,
          lang,
          config.activeMission,
          isPremium
        );
        result.feedback.aiCoachSummary = aiAdvice.summary;
      } catch {}

      /* =======================
       * 10) FINALIZATION
       * ======================= */
      result.audit_trail.audio_hash = stuHash;

      const sealed = await sealResult(result);
      setLastResultCache(sealed);

      onStageChange("DONE");
      return sealed;
    } catch (e: any) {
      onStageChange("IDLE");
      throw e;
    }
  }
}
